import React from 'react';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { Link } from '@bd/bdcore-named-routes';
import { SmoothLink } from '@bd/bdcore-utilities';
import { css } from '@bd/bdui-styles/emotion';
import { throttle } from 'lodash';

import { colors } from '../styles';

const styles = css`
  width: 100%;
  padding: 0;
  text-align: center;
  position:relative;
  z-index: 3;
  margin: 0 auto;
  font-size: 18px;
  background-color: ${colors.color1};
  border-bottom: 3px solid #FFFFFF;
  @media (min-width: 992px) {
    max-width: none;
    padding: 0;
    transition: all .3s ease-out
  }
  .PageSubMenu_title {
    color: #FFFFFF;
    background-color: transparent;
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    height: 50px;
    position: relative;
    padding: 0 22px;
    cursor: pointer;
    height: 61px;
    font-size: 15px;
    letter-spacing: 0.47px;
    margin: 0 auto;
    @media(min-width: 992px) {
      height: 85px;
      font-size: 23px;
      letter-spacing: 0.72px;
    }
    @media(min-width: 0\0) {
      position: static !important;
    }
    &::after {
      content: '';
      width: 15px;
      height: 1.2px;
      background-color: ${colors.color2};
      position: absolute;
      right: -15px;
      @media(min-width: 992px) {
        width: 22px;
        height: 2px;
        right: -30px;
      }
    }
    &::before {
      content: '';
      width: 15px;
      height: 1.2px;
      background-color: ${colors.color2};
      transform: rotate(-270deg);
      position: absolute;
      right: -15px;
      @media(min-width: 992px) {
        width: 22px;
        height: 2px;
        right: -30px;
      }
    }
    &:focus {
      outline: none;
    }
  }
  .PageSubMenu_titleAlt {
    color: #393939;
    &::before {
      content: none;
    }
  }
  .PageSubMenu_list {
    padding: 0;
    text-align: left;
    width: 100%;
    display: none;
    position: absolute;
    left: 0;
    top: 61px;
    list-style: none;
    box-shadow: 0 2px 4px 0 rgba(0,0,0,0.25);
    background-color: #1F1F1F;
    padding: 50px 30px;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    @media(min-width: 992px) {
      top: 80px;
    }
    &.open {
      display: flex;
    }
  }
  .PageSubMenu_listItem {
    width: 90%;
    max-width: 600px;
    display: flex;
    justify-content: center;
    & + li {
      border-top: 1px solid #151515;
    }
    a {
      font-weight: 700;
      color: #FFFFFF;
      font-size: 16px;
      letter-spacing: 1.81px;
      line-height: 51px;
      text-align: center;
      text-transform: uppercase;
      transition: all .3s ease-out;
      @media(min-width: 992px) {
        font-size: 19px;
        letter-spacing: 2.71px;
        line-height: 51px;
      }
      &.active, &:hover {
        color: ${colors.color1};
        font-weight: 700;
      }
    }
  }
`;

class PageSubMenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdown: false,
      stuck: false,
    };

    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.addWrapperRef = this.addWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.closeDropdown = this.closeDropdown.bind(this);
    this.handleScroll = throttle(this.handleScroll.bind(this), 50);
    this.onScroll = this.onScroll.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mouseup', this.handleClickOutside);
    document.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    document.removeEventListener('mouseup', this.handleClickOutside);
    document.removeEventListener('scroll', this.handleScroll);
  }

  onScroll(stuck) {
    this.setState({ stuck });
  }

  handleScroll() {
    const scrollAmount = window.pageYOffset || document.documentElement.scrollTop;
    if (window.matchMedia('(min-width: 992px)').matches) {
      const stuck = scrollAmount > 200;
      if (stuck !== this.state.stuck) {
        this.onScroll(stuck);
      }
    }
  }

  toggleDropdown(e) {
    e.preventDefault();

    this.setState({
      dropdown: !this.state.dropdown,
    });
  }

  closeDropdown() {
    const { onSelect } = this.props;

    if (onSelect) {
      onSelect();
    }

    this.setState({
      dropdown: false,
    });
  }

  addWrapperRef(node) {
    if (!this.wrapperRefs) {
      this.wrapperRefs = [];
    }
    this.wrapperRefs.push(node);
  }

  handleClickOutside(event) {
    if (this.wrapperRefs && !this.wrapperRefs.find(ref => ref && ref.contains(event.target))) {
      this.closeDropdown();
    }
  }

  render() {
    const { className, title, items, match } = this.props;
    const { dropdown, stuck } = this.state;
    return (
      <nav className={`${styles} ${className} ${stuck ? 'PageSubMenu_alt' : ''}`}>
        <div>
          <button className={`PageSubMenu_title ${dropdown ? 'PageSubMenu_titleAlt' : ''}`} ref={this.addWrapperRef} onClick={this.toggleDropdown}>{title}</button>
          <ul className={`PageSubMenu_list ${dropdown ? 'open' : ''}`} ref={this.addWrapperRef}>
            {
              items.map(item => {
                if (item.hide) {
                  return null;
                }
                if (item.name) {
                  return (
                    <li className="PageSubMenu_listItem" key={item.title}>
                      <Link
                        name={item.name}
                        keepParams={item.keepParams}
                        params={item.params}
                        activeExact={item.activeExact}
                        onClick={this.closeDropdown}
                      >
                        {item.title}
                      </Link>
                    </li>
                  );
                } else if (item.id) {
                  return (
                    <li className="PageSubMenu_listItem" key={item.id}>
                      <SmoothLink
                        href={item.id}
                        tolerance={item.tolerance}
                        onClick={this.closeDropdown}
                      >
                        {item.title}
                      </SmoothLink>
                    </li>
                  );
                }
                return (
                  <li className="PageSubMenu_listItem" key={item.path}>
                    <a
                      href={item.path}
                      className={`${(match.path === item.path) ? 'active' : ''}`}
                      onClick={this.closeDropdown}
                    >
                      {item.title}
                    </a>
                  </li>
                );
              })
            }
          </ul>
        </div>
      </nav>
    );
  }
}

PageSubMenu.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      name: PropTypes.string,
      id: PropTypes.string,
      title: PropTypes.string,
      params: PropTypes.object,
      keepParams: PropTypes.bool,
    }),
  ),
  match: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
};

PageSubMenu.defaultProps = {
  className: undefined,
  items: [],
  onSelect: undefined,
};

export default withRouter(PageSubMenu);



// WEBPACK FOOTER //
// ./src/components/PageSubMenu.js